import { IAssignEntity } from "../basics/basics.models";
import { EntityMetaDataModelOperator } from "../queries/queries.models";
import { GetCapResponse, GetListItemResponse } from "../lists/list.models";
import { BaseValidationResponse } from "../validations/validations.models";
import { Guid } from "../../utilities/guid";
import { IEntityTypeIdentifier } from "@rightship-tech/common-ui/models/basic.models";
import { IncidentGracePeriod } from '@rightship-tech/common-ui/models/incidents/incident.models';

export interface VetModel extends IAssignEntity {
    vesselId: number;
    portFromId: number;
    portToId: number;
    departureDate?: Date;
    arrivalDate?: Date;
    cargoTypeId?: number;
    incoTermId?: number;
    comments?: string;
    berth?: string;
    vetId?: number;
    liaseWithShipManagers?: boolean;
    vetForSTS?: boolean;
    isTest?: boolean;
    isPassedToRSVettingQueue?: boolean;
    charterTypeId?: number;
    businessUnitId?: number;
    transactionNumber?: string;
    parentVetId?: number;
    ruleCollectionId: number;
    timeCharterStartDate: Date;
    timeCharterEndDate: Date;
}

export enum VoyageDataSubmissionStatusEnum {
    Draft = 1,
    Submitted = 2,
    InProgress = 3,
    AwaitingResponse = 4,
    ReviewRequired = 5,
    Completed = 6,
    Cancelled = 7,
    VoyageDataSubmissionReassignment = 8,
    ReadyForReporting = 9,
    ReportingComplete = 10
}
export interface RawVoyageDataValidationErrorModel {
    columnName: string;
    validationError: string;
    rowNumber: number;
}

export interface CAEmissionsIntensityReportPerVessel {
    average: number;
    voyages: number;
    vesselSkey: number;
    customerSkey: number;
    year: number;
    dwt: number;
    ghgRating: string;
    totalGhgEmissions: number;
    totalImoGhgEmissions: number;
    totalDistance: number;
    isVerified: boolean;
    emissionsRatingPlus: boolean;
    imo: string;
    vesselSupplier: string;
    vesselName: string;
    vesselType: string;
    totalDenominator: number;
}

export interface CAReportPerCargoTypeForEmissionsIntensity {
    average: number;
    totalGhgEmissions: number;
    totalDistance: number;
    totalDwt: number;
    totalTeu: number;
    totalCBM: number;
    cargoTypes: number;
    vesselTypes: number;
    voyages: number;
    customerDateQuarterSkey: number;
    year: number;
    customerSkey: number;
    cargoTypeSkey: number;
    dateQuarterName: string;
    cargoTypeName: string;
    cargoUnit: string;
    totalCargo: number;
}

export interface CAEmissionsIntensityReportSummary {
    average: number;
    trajectoryValue: number;
    periodValue: number;
    year: number;
    customerSkey: number;
    computedUnit: string;
}

export interface CarbonAccountingEmissionsIntensityReportResponse {
    caEmissionsIntensityReportPerVessel: CAEmissionsIntensityReportPerVessel[];
    caReportPerCargoTypeForEmissionsIntensity: CAReportPerCargoTypeForEmissionsIntensity[];
    caEmissionsIntensityReportSummary: CAEmissionsIntensityReportSummary[];

}
export interface CarbonAccountingEmissionsIntensityReportRequest {
    emissionIntensityId: number;
    vesselTypeId: number[];
    vesselTypeGroupId: number[];
    sizeGroupeTypeId: number;
    reportTypeId: number;
    yearFrom: number;
    yearTo: number;
    customerId: number;
    isQuarterly: boolean;
    customFilterValue1?: string;
    customFilterValue2?: string;
    customFilterValue3?: string;
    customFilterValue4?: string;
    customFilterValue5?: string;
    customFilterValue6?: string;
    cargoUnit: string;
}

export interface VoyageDataSubmissionModel extends IAssignEntity {
    voyageDataSubmissionId?: number;
    year?: number;
    financialYearReportingPeriodId?: number;
    ballastLegsIncluded: boolean;
    financialYear: number;
    reportingPeriod?: {
        reportingPeriodTypeId?: number;
    }
}

export interface SaveVetResponse extends BaseValidationResponse {
    generatedId?: number;
}

export interface SaveVoyageDataSubmissionResponse extends BaseValidationResponse {
    generatedId?: number;
    validationErrors: RawVoyageDataValidationErrorModel[];
}

export interface SubtaskTypeModel {
    subtaskTypeId: number;
    name: string;
    template: string;
    isActive: boolean;
    isCloseConversation: boolean;
    usedForConsolidationEmail: boolean;
    excludeFromHome: boolean;
    isDefault: boolean;
    rfiTemplateCategoryId: number;
    isCustomerRfi: boolean;
}

export interface SaveSubtaskTypeRequest {
    subtaskTypeId: number;
    name: string;
    template: string;
    isActive: boolean;
    isCloseConversation: boolean;
    usedForConsolidationEmail: boolean;
    isDefault: boolean;
    rfiTemplateCategoryId: number;
    excludeFromHome: boolean;
    isCustomerRfi: boolean;
}

export interface SaveSubtaskTypeResponse extends BaseValidationResponse {
}

export interface CorrespondenceResponse {
    subtaskId: number;
    subtaskStatusId: number;
    guid: string;
    modifiedBy: string;
    modifiedOn: Date;
    vesselName: string;
    isInvitation?: boolean;
    vesselImo: string;
    requestorName: string;
    correspondence: CorrespondenceClientModel[];
    subtaskStatusHistory: SubtaskStatusHistoryClientModel[];
    entityTypeId?: number;
}

export interface EmailInvitationCorrespondenceResponse {
    id: string;
    encodedEmail: string;
}

export interface CorrespondenceReplyResponse extends BaseValidationResponse {
    subtaskCorrespondenceId: number;
}

export interface CorrespondenceClientModel {
    user: string;
    createdDate: Date;
    message: string;
    isFromRightShip: boolean;
    subtaskCorrespondenceId?: number;
    role: string;
    emails: SubtaskCorrespondenceEmailModel[];
}

export interface SubtaskIdentifierModel {
    isSuccess: boolean;
    subtaskId: number;
    actualSubtaskId?: number;
}

export interface SubtaskModel {
    message: string;
    isInvitation: boolean;
    statusSnippet: string;
    comment: string;
    subtaskCorrespondenceEmails: SubtaskCorrespondenceEmailModel[];
    subtaskTypeIds: number[];
    changeStatusToAwaitingResponse: boolean;
}

export interface SubtaskSubtaskTypeModel {
    subtaskTypeId: number;
}

export interface RuleConfigSubtaskTypeModel {
    subtaskTypeId: number;
}

export interface SubtaskCorrespondenceEmailModel {
    subtaskCorrespondenceEmailId?: number;
    email: string;
    emailRecipientTypeId?: number;
    canEdit?: boolean;
    isFrom?: boolean;
    isShipManagerEmail?: boolean;
    isFromConfig?: boolean;
    isTechnicalManagerEmail?: boolean;
    isInspectingCompanyEmail?: boolean;
    recipientType?: string;
}

export interface AddSubtaskRequest {
    entityId: number;
    entityTypeId: number;
    emails: { email: string, emailRecipientTypeId: number }[];
    isInternal: boolean;
    orderGuid?: string;
    isPriority?: boolean;
    isCustomerRFI?: boolean;
    resendEmail: boolean;
    statusSnippet: string;
    comment: string;
    name?: string;
    changeStatusToAwaitingResponse: boolean;
    changeStatusToPendingReview?: boolean;
    message: string;
    subtaskId: number;
    skipInvitation: boolean;
    subtaskCorrespondenceId?: number;
    submit: boolean;
    subtaskTypes: number[];
    linkedEntityIds: number[];
    linkedEntityTypeId: number;
    attachments: File[];
    files: any[];
    dataSourceId: number;
    changeStatusToPendingTerminalReview?: boolean;
}

export interface VetTaskClientModel {
    isOverriddenAtCustomer: boolean;
    element?: HTMLElement;
    prefetchRuleActionId?: number;
    hasPendingPrefetchData?: boolean;
    vettingCommentDateModified?: Date;
    createdDate?: Date;
    thirdPartyPayload?: string;
    thirdPartyPayloadDate?: Date;
    passedVettingComment: string;
    failedVettingComment: string;
    naVettingComment: string;
    infoVettingComment: string;
    ruleMeta: string;
    forceIncludeComment: boolean;
    hasThirdParty?: boolean;
    isReviewed: boolean;
    displaySections: any[];
    vettingCommentAction?: number;
    prefetchTimeToWaitMinutes?: number;
    listItems?: GetListItemResponse[];
    hasPscInspections: boolean;
    hasCap: boolean;
    capResponse: GetCapResponse;
    hasGhgVerifications: boolean;
    hasInspections: boolean;
    hasCfrs: boolean;
    hasIncidents: boolean;
    hasForms: boolean;
    hasTq?: boolean;
    hasListItems: boolean;
    hasRsListItems: boolean;
    vetSuperVetTaskOutcomeId: VetRuleOutcomeStepEnum;
    lastModifiedDate?: Date;
    vetSuperLastModifiedDate?: Date;
    ruleLastModifiedDate?: Date;
    vetSuperLastModifiedName?: string;
    rightshipPayload?: string;
    vetTaskId: number;
    ruleConfigId?: number;
    newUpdateDate?: Date;
    description: string;
    title: string;
    category: string;
    categoryIsCustomer?: boolean;
    actionToTake: string;
    reason: string;
    vetTaskOutcomeId: number;
    canViewDisplayResultsInOverview: boolean;
    canViewDisplayResultsInCriteria: boolean;
    isCritical?: boolean;
    categoryId: number;
    isManual: boolean;
    computedOutcome: boolean;
    ruleActionId: number;
    workflowStateId: number;
    systemError: string;
    ruleSubtaskTypes: number[];
    pluginEntityTypeId: number;
    sequenceNumber?: number;
    ruleCode?: string;
    vesselApplicationFormId?: number;
    isVisibleToRsVetSupersOnly: boolean;
    isInfoOnlyRule: boolean;
    prefetchDataPersistedOn: Date;
    metaData: VetTaskMetaData;
    screeningOverviewDisplayVisibilityId: number;
    screeningCriteriaDisplayVisibilityId: number;
    vettingOverviewDisplayVisibilityId: number;
    vettingCriteriaDisplayVisibilityId: number;
}

export interface VetTaskMetaData {
    category: string;
    name: string;
    expiryDate?: Date;
}

export interface SafetyRatingReviewTaskClientModel {
    isOverriddenAtCustomer: boolean;
    element?: HTMLElement;
    safetyRatingReviewCommentDateModified?: Date;
    vettingComment: string;
    isReviewed: boolean;
    hasListItems: boolean;
    listItems?: GetListItemResponse[];
    vetSuperSafetyRatingReviewTaskOutcomeId: number;
    safetyRatingReviewTaskOutcomeId?: SafetyRatingReviewOutcomeStepEnum;
    lastModifiedDate?: Date;
    vetSuperLastModifiedDate?: Date;
    ruleLastModifiedDate?: Date;
    vetSuperLastModifiedName?: string;
    safetyRatingReviewTaskId: number;
    canOverrideComputedFailedOutcome?: boolean;
    systemRuleConfigId?: number;
    newUpdateDate?: Date;
    description: string;
    title: string;
    category: string;
    actionToTake: string;
    reason: string;
    categoryId: number;
    computedOutcome: boolean;
    sequenceNumber?: number;
    ruleCode?: string;
}

export interface VetRecentActivityClientModel {
    entityTypeId: number;
    isRfi: boolean;
    isForm: boolean;
    isAwaitingResponse: boolean;
    isPendingReview: boolean;
    isOpen: boolean;
    entityId: number;
    statusId: number;
    title: string;
    status: string;
    statusSnippet: string;
    statusSnippet2: string;
}

export interface TargetEmissionsReportingPeriodModel {
    carbonEmission: number;
    year: number;
    monthNumber: number;
    sequenceNumber: number;
    financialYearTypeId: number;
    reportingPeriod: string;
    yearGuid: Guid;
}

export interface CarbonAccountingCompanyReportingResultModel {
    year: number;
    financialYear: number;
    reportingPeriodTypeId: number;
    reportingPeriodId: number;
    carbonEmission: number;
    ghgIntensity: number;
    monthNumber: number;
    percentageDifference: number;
    target: number;
    reportingPeriod: string;
    reportingPeriodType: string;
    sequenceNumber: number;
    financialYearTypeId: number;
    yearGuid: Guid;
    isFirstYear: boolean;
}

export interface VoyageDataSubmissionDashboardDataModel {
    fromYear: number;
    toYear: number;
    carbonEmissionReductionTarget: number;
    ghgIntensityReductionTarget: number;
    runningTotalCarbonEmissions: number;
    estimatedEmmissionsForCompletedPeriods: number;
    totalEstimatedEmissions: number;
    currentReductionPercentage: number;
    effectiveFrom: Date;
    effectiveTo: Date;
    reportingPeriodTypeId: number;
    isOnTrack: boolean;
    targetEmissionsPerReportingPeriod: TargetEmissionsReportingPeriodModel[];
    companyReportingResults: CarbonAccountingCompanyReportingResultModel[];
    yearlyTotalDataResults: YearlyTotalDataModel[];
    goalTrackerTargetEmissions: TargetEmissionsReportingPeriodModel[];
    defaultVoyageIncludesBallastLeg: boolean;
}

export interface YearlyTotalDataModel {
    totalEmissions: number;
    totalIntensity: number;
    financialYear: number;
}

export interface VoyageDataSubmissionClientModel extends IEntityTypeIdentifier {
    id: number;
    entityType: number;
    voyageDataSubmissionId: number;
    createdDate: Date;
    createdBy: string;
    identifier: string;
    createdByBusinessUnit: string;
    voyageDataSubmissionStatusComment: string;
    customer: string;
    voyageDataSubmissionStatus: string;
    createdByEmail: string;
    createdByBusinessUnitId: number;
    voyageDataSubmissionStatusId: number;
    previousVoyageDataSubmissionStatusId: number;
    assignedUserId: number;
    assignedTo?: string;
    createdByUserId: number;
    createdByRoleId: number;
    customerId: number;
    correspondenceCount: number;
    businessUnitId: number;
    documentsCount: number;
    privateNotesCount: number;
    isHostedCustomer: boolean;
    isHosted: boolean;
    voyageDataSubmissionStatusCreatedDate: Date;
    voyageDataSubmissionCorrespondenceEmails: SubtaskCorrespondenceEmailModel[];
    voyageDataSubmissionStatusHistory: VoyageDataSubmissionStatusHistoryClientModel[];
    lastComputedDate: Date;
    ballastLegsIncluded: boolean;
}

export enum ReportingPeriodTypeEnum {
    Annual = 1,
    SemiAnnual = 2,
    Quarterly = 3,
    Monthly = 4
}

export interface VettingSireReport {
    inspectionDate: Date;
    inspectionPort: string;
    flag: string;
    inpectingCompany: string;
    inspectorName: string;
    inspectionOperation: string;
    dWT: number;
    operatingCompany: string;
    yearBuilt: Date;
    hullType: string;
    classificationSociety: string;
    vettingOrganisation: string;
    requestingMemberOrganisation: string;
    numberOfIncidents: string;
    documentType: string;
    vpqLastUpdated: Date;
    crewDocumentLastUpdated: Date;
    documentDownloadsValidUntil: Date;
    vettingSireReportItems: VettingSireReportItem[];
}

export enum VettingSireReportType {
    VIQ = 1,
    VPQ = 2,
    CrewMatrix = 3,
    IncidentReport = 4,
    PSCSummary = 5
}

export interface VettingSireReportItemAttachment {

    attachmentName: string;
    attachmentMimeType: string;
    attachmentUrl: string;
}

export interface VettingSireReportItem {
    questionNumber: string;
    inspectorComments: string;
    inspectorObservations: string;
    operatorCommentsInitial: string;
    operatorCommentsSubsequent: string;
    response: string;
    score: number;
    attachments: VettingSireReportItemAttachment[];
    vettingSireReportItemReponseTypeId: number;
}

export interface VetRiskScoreModel {
    riskScore: { nominator: number; denominator: number; result: number; };
    title: string;
    name: string;
    riskLevel: string;
    riskLevelId: number;
    sequence: number;
    riskCategoryId: number;

    countLowFail: number;
    countLowPass: number;
    countMediumFail: number;
    countMediumPass: number;
    countHighFail: number;
    countHighPass: number;
    countOraFail: number;
    countOraPass: number;
}

export interface VetRuleRunItemScoreClientModel {
    vettingRuleRunItemId: number;
    riskCategory: string;
    riskLevel: string;
    riskLevelId: number;
}

export interface VetRuleRunItemClientModel {
    vetId: number;
    vetTaskId: number;
    wasActive: boolean;
    isActive: boolean;
    previousVetTaskOutcome: number;
    vetTaskOutcome: number;
    sequenceNumber: number;
    vettingRuleRunId: number;
    prefetchRuleActionId: number;
    vettingRuleRunItemId: number;
    date: Date;
    ruleCode: string;
    title: string;
    description: string;
    scores: VetRuleRunItemScoreClientModel[];
    previousScores: VetRuleRunItemScoreClientModel[];
    hasSystemError: boolean;
    hasPreviousSystemError: boolean;
    hasPendingPrefetchData?: boolean;
    hasListItems?: boolean;
    hasRsListItems?: boolean;
    hasCap?: boolean;
    canViewDisplayResultsInOverview?: boolean;
}


export interface VetRuleRunRiskClientModel {
    riskCategory: string;
    riskLevel: string;
    riskLevelId: number;
    riskScore: { nominator: number, denominator: number, result: number };

    countLowFail: number;
    countLowPass: number;
    countMediumFail: number;
    countMediumPass: number;
    countHighFail: number;
    countHighPass: number;
    countOraFail: number;
    countOraPass: number;
    countChangedFail: number;
    countChangedPass: number;
}

export interface VetRuleRunClientModel {
    isSelected?: boolean;
    vettingRuleRunId: number;
    date: Date;
    action: string;
    vetStatus: string;
    businessUnitId: number;
    items: VetRuleRunItemClientModel[];
    risks: VetRuleRunRiskClientModel[];
}

export interface RuleActionModel {
    ruleActionId: number;
    title: string;
    description: string;
    link: string;
    linkTitle: string;
    info: string;
}

export interface VetClientModel extends IEntityTypeIdentifier {
    vesselPortCallId?: number;
    viewModel?: string;
    fullVetId?: number;
    fullVetRequestedDate?: Date;
    portCountryOfLoadUnknown: any;
    portCountryOfDischargeUnknown: any;
    vetId: number;
    requestedByEmail?: string;
    requireCustomerDecision?: boolean;
    customerId?: number;
    isHostedCustomer?: boolean;
    isHosted?: boolean;
    isPassedToRSVettingQueue?: boolean;
    businessUnitId?: number;
    docCompanyId: number;
    docCompanyName?: string;
    otherComments: string;
    vesselId: number;
    vetStatusId: number;
    vettingTypeId?: number;
    vetStatus: string;
    customerDecisionStatusId?: number;
    previousCustomerDecisionStatusId?: number;
    previousCustomerDecisionStatusDate?: Date;
    previousCustomerDecisionStatusBy?: string;
    isMigratedVet?: boolean;
    customerDecisionStatusDate?: Date;
    customerDecisionStatusBy?: string;
    customerDecisionComment?: string;
    yearOfConstruction?: number;
    dateRequested: Date;
    dateOfDeparture?: Date;
    dateOfArrival?: Date;
    isTest?: boolean;
    built?: Date;
    deadWeightTonnage: number;
    requestedBy: string;
    phoneNumbers: string[];
    cargo: string;
    portOfDischarge: string;
    portOfLoad: string;
    assignedUserId: number;
    assignedTo?: string;
    internationalMaritimeOrganizationNumber: string;
    cargoTypeCategoryId: number;
    documentOfComplianceEmail: string;
    vetRequiresMoreInformationEmails: SubtaskCorrespondenceEmailModel[];
    vetRiskScores?: VetRiskScoreModel[];
    vehicleIdentificationNumber: string;
    europeanNumberOfIdentification: string;
    vesselName: string;
    vesselType: string;
    incoTerms: string;
    vetComments: string;
    berth: string;
    vetTasks: VetTaskClientModel[];
    vetStatusHistory: VetStatusHistoryClientModel[];
    vetCorrespondenceEmails?: SubtaskCorrespondenceEmailModel[];
    initialEmailsForCorrespondence?: SubtaskCorrespondenceEmailModel[];
    portOfLoadId?: number;
    portOfDischargeId?: number;
    cargoId?: number;
    incoTermsId?: number;
    requestedByBusinessUnitId?: number;
    requestedByBusinessUnit?: string;
    portCountryOfLoad?: string;
    portCountryOfDischarge?: string;
    requestedByUserId?: number;
    liaseWithShipManagers?: boolean;
    vetForSTS?: boolean;
    starRating: number;
    pendingEmails?: boolean;
    rating: string;
    portCountryOfDischargeSanctioned: boolean;
    portCountryOfLoadSanctioned: boolean;
    documentsCount?: number;
    privateNotesCount?: number;
    correspondenceCount?: number;
    isVhr?: number;
    transactionNumber?: string;
    ageForInspection?: number;
    dwtForInspection?: number;
    showVetBanner?: boolean;
    failedRightShipCriteria?: boolean;
    failedCustomerCriteria?: boolean;
    parentVetId?: number;
    parentVetRuleCollectionName?: string;
    ruleCollectionName?: string;
    isRightStoreVet?: boolean;
    daysRemainingAtVetOutcome?: number;
    daysToAgeRequirementAtVetOutcome?: number;
    rightShipInspectionExpiryDateAtVetOutcome?: Date;
    inspectionStateAtVetOutcome?: number;
    crewWelfareValidUntilAtVetOutcome?: Date;
    crewWelfareStatusAtVetOutcome?: string;
    starRatingAtVetOutcome?: number;
    safetyRatingTypeAtVetOutcome?: number;
    ratingAtVetOutcome?: string;
    ratingPlusAtVetOutcome?: boolean;
    isRatingVerifiedAtVetOutcome?: boolean;
    requestedOnBehalfOfCompanyName: string;
    requestedOnBehalfOfName: string;
    requestedOnBehalfOfEmail: string;
    incidentGracePeriodsAtVetOutcome?: IncidentGracePeriod[];
    charterTypeId?: number;
    timeCharterEndDate?: Date;
}

export interface OpenVetModel {
    openVetId: number;
    vesselId: number;
    vetId: number;
    businessUnitId: number;
    vetDate: Date;
}

export interface SetCustomerDecisionResponse {
    vetId: number,
    comments: string,
    username: string,
    date?: Date
}

export interface SafetyRatingReviewClientModel extends IEntityTypeIdentifier {
    safetyRatingReviewId: number;
    requestedByEmail?: string;
    customerId?: number;
    isHostedCustomer?: boolean;
    businessUnitId?: number;
    docCompanyId: number;
    otherComments: string;
    vesselId: number;
    safetyRatingReviewStatusId: number;
    safetyRatingReviewStatus: string;
    starRatingAtSafetyRatingReviewOutcome: number;
    safetyRatingTypeAtSafetyRatingReviewOutcome: number;
    starRatingAtSafetyRatingReviewAfterOutcome: number;
    safetyRatingTypeAtSafetyRatingReviewAfterOutcome: number;
    yearOfConstruction?: number;
    dateRequested: Date;
    built?: Date;
    deadWeightTonnage: number;
    requestedBy: string;
    phoneNumbers: string[];
    assignedUserId: number;
    assignedTo?: string;
    internationalMaritimeOrganizationNumber: string;
    documentOfComplianceEmail: string;
    safetyRatingReviewRequiresMoreInformationEmails: SubtaskCorrespondenceEmailModel[];
    vehicleIdentificationNumber: string;
    europeanNumberOfIdentification: string;
    vesselName: string;
    vesselType: string;
    safetyRatingReviewComments: string;
    safetyRatingReviewTasks: SafetyRatingReviewTaskClientModel[];
    safetyRatingReviewStatusHistory: SafetyRatingReviewStatusHistoryClientModel[];
    safetyRatingReviewCorrespondenceEmails?: SubtaskCorrespondenceEmailModel[];
    initialEmailsForCorrespondence?: SubtaskCorrespondenceEmailModel[];
    requestedByBusinessUnitId?: number;
    requestedByBusinessUnit?: string;
    requestedByUserId?: number;
    starRating: number;
    rating: string;
    documentsCount?: number;
    privateNotesCount?: number;
    correspondenceCount?: number;
    anyRfi?: boolean;
    incidentGracePeriodsOutcome: IncidentGracePeriod[]; 
    incidentGracePeriodsAfterOutcome: IncidentGracePeriod[]; 
}

export interface SubtaskStatusHistoryClientModel {
    createdDate: Date;
    status: string;
    statusId: number;
    user: string;
    comment: string;
}

export interface VetStatusHistoryClientModel {
    createdDate: Date;
    status: string;
    previousStatusId?: number;
    statusId: number;
    user: string;
    reasonForCancellation: string;
    comment: string;
    fullComment: string;
    subtaskTypes: number[];
    isAutoCancelled?: boolean;
    failedRightShipCriteria?: boolean;
    failedCustomerCriteria?: boolean;
    statusLabel?: string;
}

export interface VoyageDataSubmissionStatusHistoryClientModel {
    createdDate: Date;
    status: string;
    previousStatusId?: number;
    statusId: number;
    user: string;
    comment: string;
    fullComment: string;
    subtaskTypes: number[];
}

export interface SafetyRatingReviewStatusHistoryClientModel {
    createdDate: Date;
    status: string;
    previousStatusId?: number;
    statusId: number;
    userId?: number;
    user: string;
    comment: string;
    fullComment: string;
    subtaskTypes: number[];
}

export interface RuleConfigBroker {
    model?: RuleConfigBrokerSaveModel;
    onSubmit?: (() => boolean);
    parameters?: RuleConfigBrokerParameters;
}

export interface RuleConfigBrokerSaveModel {
    date?: string;
    dateDetentions?: string;
    linkedIncidents?: number[];
    linkedPscInspections?: number[];
    filterByDetentions?: boolean;
    isManual?: boolean;
    filterByDeficiencies?: boolean;
    isAnd?: boolean;
    outstandingDefects?: number;
    outstandingDetentions?: number;
    filterOperator?: EntityMetaDataModelOperator;
    filterOperatorDetentions?: EntityMetaDataModelOperator;
    onboardingConfigId?: number;
    subtaskTypeId?: number;
    subtaskTypeMessage?: string;
    name?: string;
    category?: string;
}

export interface RuleConfigBrokerParameters {

}

export enum VetRuleOutcomeStepEnum {
    Pass = 1,
    Fail = 2,
    NA = 3
}

export enum SafetyRatingReviewOutcomeStepEnum {
    Pass = 1,
    Fail = 2
}

export enum InspectionDeficiencyOutcomeStepEnum {
    Pass = 1,
    Fail = 2
}

export enum VettingCommentActionEnum {
    AlwaysIncludeComment = 1,
    NeverIncludeComment = 2,
    OnlyShowIfFailed = 3
}

export enum VettingStepEnum {
    VetOverview = 1,
    VetCriteria = 2,
    VetDocuments = 3,
    VetComments = 4,
    VetVesselCorrespondence = 5,
    EmailHistory = 6,
    HistoryTrail = 7,
    VetRulesRun = 8
}

export enum AutoScreenStepEnum {
    AutoScreenOverview = 1,
    VetCriteria = 2,
    VetDocuments = 3,
    VetComments = 4,
    VetVesselCorrespondence = 5,
    EmailHistory = 6,
    HistoryTrail = 7,
    VetRulesRun = 8
}

export enum VoyageDataSubmissionStepEnum {
    Overview = 1,
    Documents = 2,
    PrivateNotes = 3,
    Correspondence = 4,
    HistoryTrail = 5,
    VoyageRecords = 6
}

export enum SafetyRatingReviewStepEnum {
    SafetyRatingReviewOverview = 1,
    SafetyRatingReview = 2,
    SafetyRatingReviewDocuments = 3,
    SafetyRatingReviewCorrespondence = 4,
    SafetyRatingReviewEmailHistory = 5,
    SafetyRatingReviewComments = 6,
    SafetyRatingReviewHistoryTrail = 7
}

export enum VettingActionStepEnum {
    ManualAction = 1,
    RequireTQForm = 2,
    RequireInspection = 3,
    RequirePSCReview = 4,
    RequireIncidentReview = 5,

    VesselHealthCheckCritical = 200
}

export enum VetTaskGroupingEnum {
    RequireReview = 1,
    NoReviewRequired = 2
}

export enum VetStatusEnum {
    NotSet = -1,
    Processing = 1,
    Pending = 2,
    InProgress = 3,
    AwaitingResponse = 4,
    PendingReview = 5,
    Accepted = 6,
    Rejected = 7,
    Cancelled = 8,
    VetReassignment = 9,
    Expired = 10,
    Escalated = 11,
    ProcessingOutcome = 12,

    AS_Failed = 100,
    AS_ExceptionRequired = 101,
    AS_Passed = 102,
    
    DD_AwaitingResponse = 301,
    DD_Acceptable = 302,
    DD_Unacceptable = 303,
    DD_DueDiligenceReassignment = 304,
    DD_RequireIntervention = 305,
    DD_RequireRightShipIntervention = 306,
    DD_PendingSeniorApproval = 307,
    DD_UnderReview = 308,

    Completed = 200
}

export enum CustomerDecisionStatusEnum {
    Pending = 1,
    Unacceptable = 2,
    Acceptable = 3,
    Cancelled = 4
}

export enum VettingType {
    FullVet = 1,
    AutoScreening = 2,
    DueDiligence = 3
}

export enum SafetyRatingReviewStatusEnum {
    Processing = 1,
    New = 2,
    InProgress = 3,
    AwaitingResponse = 4,
    PendingReview = 5,
    Accepted1 = 6,
    Accepted2 = 7,
    Rejected = 8,
    SafetyRatingReviewReassignment = 9,
    AcceptedFromSystem = 10,
    Canceled = 11
}

export enum FormsStatusEnum {
    ComputingRules = "Computing Rules",
    PendingFormInput = "Pending Form Input",
    PendingTerminalReview = "Pending Terminal Review",
    PendingRightshipReview = "Pending RightShip Review",
    Completed = "Completed",
    InProgress = "In Progress",
    AwaitingResponse = "Awaiting Response",
    ReviewRequired = "Review Required",
    NewSubmission = "New Submission",
    Acceptable = "Acceptable",
    Unacceptable = "Unacceptable",
    Cancelled = "Cancelled",
    AcceptableDayOnly = "Acceptable Day Only",
    UnderReview = "Under Review"
}

export enum SubtaskStatusEnum {
    Pending = 1,
    PendingReview = 2,
    AwaitingResponse = 3,
    Closed = 6,
    Replied = 7
}

export enum PscInspectionStatusEnum {
    Created = 1,
    InProgress = 2,
    AwaitingResponse = 3,
    Misreported = 4,
    PscInspectionReassignment = 5,
    Closed = 6,
    PendingReview = 7
}

export enum IncidentStatusEnum {
    Created = 1,
    InProgress = 2,
    AwaitingResponse = 3,
    Misreported = 4,
    IncidentReassignment = 5,
    Closed = 6,
    PendingReview = 7
}

export enum RfiTemplateCategoryEnum {
    VET = 1,
    PSC = 2,
    Incident = 3,
    TVQ = 4,
    FBR = 5,
    GHG = 6,
    CFR = 7,
    RightShipInspection = 8,
    RightShipInspectionUnacceptable = 9,
    RightShipInspectionInProgress = 11,
    TankerInspection = 13,
    DataSubmission = 14,
    SafetyRatingReview = 15,
    VoyageDataSubmission = 16,
    CrewWelfare = 17,
    DocumentManagement = 80
}

export enum EmailRecipientTypeEnum {
    To = 1,
    Cc = 2,
    Bcc = 3
}

export enum VettingActionEnum {
    VetIsRequested = 1,
    VetHasAStatusChange = 2,
    VetHasAnOutcome = 3,
    VetOutcomeIsUnacceptable = 4,
    VetIsExpiringExpired = 5,
    VetRequiresFurtherInformation = 6,

    TQAwaitingResponse = 7,
    TQRequireReview = 8,

    HQAwaitingResponse = 9,
    HQRequireReview = 10,
    HQAcceptableDayOnly = 16,

    GHGIsRequested = 11,
    GHGHasAStatusChange = 12,
    GHGHasAnOutcome = 13,
    GHGOutcomeIsUnacceptable = 14,
    GHGRequiresFurtherInformation = 15,
    TQComplete = 17,
    CAReportingPeriodExpire = 25,
    VoyageDataHasAStatusChange = 26,

    FBRRequiresFurtherInformation = 27,
    EmailFormRequested = 28,

    WindwardVoiFails = 29,

    RequiredRFI = 50,

    DueDiligenceIsRequested = 200,
    DueDiligenceHasAnOutcome = 201,
    DueDiligenceRequireIntervention = 202,
    DueDiligenceHasStatusChange = 203
}

export class GroupedVoyageValidationErrorModel {
    groupIdentifier: string;
    count: number;
    validationError: string;
    validationTypeId: number;
}

export interface GetVoyageDataSubmissionComputationErrorsResponse {
    errors?: VoyageDataSubmissionComputationErrorModel[];
    errorCount?: number;
}

export interface VoyageDataSubmissionComputationErrorModel {
    id: string;
    issue: string;
}

export interface VoyageDataSubmissionGetTotalEmissionsAndIntensityResponse {
    totals: VoyageDataSubmissionTotalEmissionsAndIntensity;
}

export interface VoyageDataSubmissionTotalEmissionsAndIntensity {
    totalEmissions: number;
    totalIntensity: number;
}

export enum VoyageValidationTypesEnum {
    Required = 1,
    Invalid = 2,
    Port = 3,
    Vessel = 4,
    CargoType = 5,
    FuelType = 6
}

export interface GetPendingVetOrdersResponse {
    pendingOrders: VetOrderClientModel[];
}

export interface VetOrderClientModel {
    orderGuid: Guid;
    purchaseDate: Date;
    userEmail: string;
    userFullName: string;
    productTypeId: number;
    ruleCollectionId?: number;
}


export interface LastFourSubmissionsSummaryModel {
    lastFourSubmissionId: number;
    totalGhgEmissions: number;
    totalIntensity: number;
    fuelBurnt: number;
    totalDistance: number;
    vessels: number;
    voyages: number;
    cargoTypes: number;
    totalDwt: number;
    totalTeu: number;
    totalCbm: number;
    voyageDataSubmissionId: number;
    isCurrentSubmission: boolean;
    createdDate: Date;
    year: number;
    reportingPeriod: string;
}

export interface CargoTypeDistributionModel {
    cargoTypeDistributionId: number;
    totalGhgEmissions: number;
    cargoUnit: string
    cargoType: string;
    periodValue: number;
    year?: number;
}

export interface LastFourSubmissionsSummaryResponse {
    lastFourSubmissionsSummary: LastFourSubmissionsSummaryModel[]
}

export interface CargoTypeDistributionResponse {
    cargoTypeDistributions: CargoTypeDistributionModel[]
}

export interface OrderAbandonRequest {
    orderGuid: string;
    viewTitle: string;
    orderPurchasedDate: string;
    vesselId: number;
    optimizlyOrderKey: string;
}

export enum RiskCategoryEnum {
    NA = -1,
    Low = 1,
    Medium = 2,
    High = 3,
}


export class RiskScoreHelper {

    static calculateRiskScore(run: { countOraFail: number, countLowFail: number, countMediumFail: number, countAllLow: number, countAllMedium: number, countHighFail: number, countAllHigh: number }) {
        if (run) {
            if (run.countOraFail >= 1) return {
                nominator: 100,
                denominator: 100,
                result: 100
            };

            const low = 1;
            const medium = (low * run.countAllLow) + 1;
            const high = (medium * run.countAllMedium) + 1;

            const riskScore = (run.countLowFail * low) + (run.countMediumFail * medium) + (run.countHighFail * high);
            const totalRiskScore = (run.countAllLow * low) + (run.countAllMedium * medium) + (run.countAllHigh * high);
            if (totalRiskScore > 0) {
                const result = Number(((riskScore / totalRiskScore) * 100).toFixed(2));
                return {
                    nominator: riskScore,
                    denominator: totalRiskScore,
                    result: result
                };
            }
        }

        return {
            nominator: 0,
            denominator: 0,
            result: 0
        };
    }
}

export enum RuleActionEnum
{
    ManualAction = 1,
    RequireForm = 2,
    RequireInspection = 3,
    RequirePSCReview = 4,
    RequireIncidentReview = 5,
    RequireGhgVerificationReview = 6,
    RequireCFRs = 7,
    RequireCustomerDecision = 8,
    ThirdParty = 9,
    RequireCap = 10,
    RequireRFI = 50,
    RequireIntervention = 51,
    DeemUnacceptable = 52,
    //Prefetch Actions
    ThirdPartyPrefetch = 100,
    WindwardPrefetch = 205,
    RefinitivPrefetch = 300,
    RefinitivReportCustomerDecision = 301,
    CssrPrefetch = 302,
    OcimfPrefetch = 303
}